<template lang="">
  <div class="job-details-page">
    <AppNavbar @login-click="goToLogin" @start-click="goToLogin" />

    <div class="job-details-breadcrumb">
      <router-link :to="{ name: 'JobsList' }" class="job-details-breadcrumb__back">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
        </svg>
        Vagas
      </router-link>
      <span class="job-details-breadcrumb__sep">›</span>
      <span class="job-details-breadcrumb__current">{{ job?.title }}</span>
    </div>

    <div v-if="jobLoading" class="job-details-loading">Carregando vaga...</div>

    <template v-else-if="job">
      <section class="job-hero">
        <div class="job-hero__left">
          <span class="job-hero__logo">{{ companyInitial }}</span>
          <div class="job-hero__title-group">
            <h1 class="job-hero__title">{{ job.title }}</h1>
            <div class="job-hero__tags">
              <span class="job-hero__company">{{ companyName }}</span>
              <span v-if="job.level" class="job-hero__tag">{{ job.level }}</span>
              <span v-if="workModelLabel" class="job-hero__tag">{{ workModelLabel }}</span>
              <span v-if="location" class="job-hero__tag">{{ location }}</span>
            </div>
          </div>
        </div>

        <div class="job-hero__reward">
          <span class="job-hero__reward-label">Recompensa por indicação</span>
          <span class="job-hero__reward-value">{{ job.rewardType?.title || 'A combinar' }}</span>
          <span class="job-hero__reward-note">por contratação efetivada</span>
        </div>
      </section>

      <div class="job-body">
        <div class="job-content">
          <section v-if="job.description" class="job-content__section">
            <h2 class="job-content__title">Sobre a vaga</h2>
            <p class="job-content__text">{{ job.description }}</p>
          </section>

          <section v-if="requirementLines.length" class="job-content__section">
            <h2 class="job-content__title">Requisitos</h2>
            <ul class="job-content__list">
              <li v-for="(line, index) in requirementLines" :key="`req-${index}`">{{ line }}</li>
            </ul>
          </section>

          <section v-if="responsibilityLines.length" class="job-content__section">
            <h2 class="job-content__title">Responsabilidades</h2>
            <ul class="job-content__list">
              <li v-for="(line, index) in responsibilityLines" :key="`resp-${index}`">{{ line }}</li>
            </ul>
          </section>
        </div>

        <aside class="job-sidebar">
          <div class="job-sidebar__cta">
            <h2 class="job-sidebar__cta-title">Fazer uma indicação</h2>
            <p class="job-sidebar__cta-desc">
              Indique um profissional da sua rede para esta vaga. Se for contratado, você recebe
              a recompensa combinada.
            </p>
            <BaseButton variant="primary" class="job-sidebar__cta-btn" @click="handleIndication">
              Indicar →
            </BaseButton>
            <p class="job-sidebar__cta-note">
              Suas indicações ficam protegidas com seu nome registrado na plataforma.
            </p>
          </div>

          <div class="job-info-card">
            <h2 class="job-info-card__title">Detalhes da vaga</h2>
            <div class="job-info-card__row">
              <span class="job-info-card__label">Empresa</span>
              <span class="job-info-card__value">{{ companyName }}</span>
            </div>
            <div v-if="location" class="job-info-card__row">
              <span class="job-info-card__label">Localização</span>
              <span class="job-info-card__value">{{ location }}</span>
            </div>
            <div v-if="workModelLabel" class="job-info-card__row">
              <span class="job-info-card__label">Modelo</span>
              <span class="job-info-card__value">{{ workModelLabel }}</span>
            </div>
            <div v-if="job.category?.title" class="job-info-card__row">
              <span class="job-info-card__label">Área</span>
              <span class="job-info-card__value">{{ job.category.title }}</span>
            </div>
            <div v-if="job.deadline" class="job-info-card__row">
              <span class="job-info-card__label">Disponível até</span>
              <span class="job-info-card__value">{{ formattedDeadline }}</span>
            </div>
          </div>
        </aside>
      </div>
    </template>

    <p v-else class="job-details-loading">Vaga não encontrada.</p>

    <AppFooter />
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import { computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AppFooter from '../../components/ui/AppFooter.vue';
import AppNavbar from '../../components/ui/AppNavbar.vue';
import BaseButton from '../../components/ui/BaseButton.vue';
import useModal from '../../hooks/useModal';
import formatDateUtils from '../../utils/formatDate';

export default {
  name: 'JobDetails',
  components: {
    AppFooter,
    AppNavbar,
    BaseButton,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const modal = useModal();

    onMounted(async () => {
      const { jobId } = route.params;
      await store.dispatch('getJobsById', { jobId });

      if (route.name === 'JobIndication' && isLogged.value) {
        modal.open({
          status: 'active',
          component: 'JobIndicationModal',
          props: { isModal: true },
          width: '980px',
        });
        router.replace({ name: 'JobDetails', params: { jobId } });
      }
    });

    const siteData = reactive({
      title: 'Indicoo - Ganhe dinheiro indicando profissionais de confiança',
      description: 'Ganhe dinheiro indicando profissionais de confiança',
    })

    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
        {
          name: 'og:title',
          content: computed(() => store.state?.jobs.job?.title),
        },
        {
          name: 'og:description',
          content: computed(() => store.state?.jobs.job?.description),
        },
        {
          name: 'og:image',
          content: 'http://www.indicoo.com.vc/img/indicoo.jpg',
        },
        {
          name: 'og:type',
          content: 'website',
        },
      ],
    });

    const job = computed(() => store.state?.jobs.job);
    const jobLoading = computed(() => store.state.jobs.jobLoading);
    const isLogged = computed(() => store.state.user.isLogged);

    const companyName = computed(() => {
      const { company } = job.value || {};
      return (typeof company === 'string' ? company : company?.name) || 'Empresa';
    });

    const companyInitial = computed(() => companyName.value.charAt(0).toUpperCase());

    const workModelLabel = computed(() => {
      const { workModel } = job.value || {};
      return typeof workModel === 'string' ? workModel : workModel?.title;
    });

    const location = computed(() => {
      if (!job.value) return '';
      const { location: loc, country } = job.value;
      return (loc && loc !== '') ? loc : country;
    });

    const toLines = (text) => (text ? text.split('\n').map((line) => line.trim()).filter(Boolean) : []);

    const requirementLines = computed(() => toLines(job.value?.requirements));
    const responsibilityLines = computed(() => toLines(job.value?.responsibilities));

    const formattedDeadline = computed(() => {
      if (!job.value?.deadline) return '';
      return formatDateUtils.formatDate(job.value.deadline).date;
    });

    const goToLogin = () => {
      router.push({ name: 'Login' });
    };

    const handleIndication = () => {
      if (!isLogged.value) {
        const redirect = `/vagas/${job.value.jobId}/indicar`;
        router.push({ name: 'Login', query: { redirect } });
        return;
      }

      modal.open({
        status: 'active',
        component: 'JobIndicationModal',
        props: { isModal: true },
        width: '980px',
      });
    };

    return {
      job,
      jobLoading,
      companyName,
      companyInitial,
      workModelLabel,
      location,
      requirementLines,
      responsibilityLines,
      formattedDeadline,
      goToLogin,
      handleIndication,
    };
  },
};
</script>

<style scoped>
.job-details-page {
  background: var(--navy-deep);
  min-height: 100vh;
}

.job-details-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 1120px;
  margin: 0 auto;
  padding: 100px 24px 0;
  font-family: var(--font-brand);
  font-size: 13px;
  color: var(--muted);
}

.job-details-breadcrumb__back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  text-decoration: none;
}

.job-details-breadcrumb__back svg {
  width: 14px;
  height: 14px;
}

.job-details-breadcrumb__sep {
  color: var(--faint);
}

.job-details-loading {
  padding: 120px 24px;
  text-align: center;
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--muted);
}

.job-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-xl);
  flex-wrap: wrap;
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px 24px 48px;
}

.job-hero__left {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}

.job-hero__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: var(--r-md);
  background: var(--cyan-glow);
  border: 1px solid var(--cyan-glow);
  font-family: var(--font-brand);
  font-size: 28px;
  font-weight: 900;
  color: var(--cyan);
}

.job-hero__title-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.job-hero__title {
  font-family: var(--font-brand);
  font-size: 32px;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--white);
}

.job-hero__tags {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.job-hero__company {
  font-family: var(--font-brand);
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
}

.job-hero__tag {
  font-family: var(--font-brand);
  font-size: 12px;
  font-weight: 600;
  color: var(--cyan);
  background: var(--cyan-glow);
  border: 1px solid var(--cyan-glow);
  border-radius: var(--r-pill);
  padding: 4px 10px;
}

.job-hero__reward {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-md);
  padding: 20px 28px;
}

.job-hero__reward-label {
  font-family: var(--font-brand);
  font-size: 12px;
  color: var(--muted);
}

.job-hero__reward-value {
  font-family: var(--font-brand);
  font-size: 32px;
  font-weight: 900;
  color: var(--cyan);
}

.job-hero__reward-note {
  font-family: var(--font-brand);
  font-size: 11px;
  color: var(--faint);
}

.job-body {
  display: flex;
  align-items: flex-start;
  gap: var(--gap-xl);
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px 96px;
}

.job-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gap-xl);
}

.job-content__section {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.job-content__title {
  font-family: var(--font-brand);
  font-size: 18px;
  font-weight: 700;
  color: var(--white);
}

.job-content__text {
  font-family: var(--font-brand);
  font-size: 15px;
  line-height: 1.75;
  color: var(--text);
  white-space: pre-line;
}

.job-content__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
}

.job-content__list li {
  position: relative;
  padding-left: 22px;
  font-family: var(--font-brand);
  font-size: 14px;
  line-height: 1.6;
  color: var(--text);
}

.job-content__list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--cyan);
}

.job-sidebar {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.job-sidebar__cta {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-lg);
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.job-sidebar__cta-title {
  font-family: var(--font-brand);
  font-size: 18px;
  font-weight: 700;
  color: var(--white);
}

.job-sidebar__cta-desc {
  font-family: var(--font-brand);
  font-size: 13px;
  line-height: 1.6;
  color: var(--muted);
}

.job-sidebar__cta-btn {
  width: 100%;
}

.job-sidebar__cta-note {
  font-family: var(--font-brand);
  font-size: 11px;
  line-height: 1.5;
  color: var(--faint);
  text-align: center;
}

.job-info-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-md);
  padding: 24px;
}

.job-info-card__title {
  font-family: var(--font-brand);
  font-size: 14px;
  font-weight: 700;
  color: var(--white);
}

.job-info-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-md);
}

.job-info-card__label {
  font-family: var(--font-brand);
  font-size: 13px;
  color: var(--muted);
}

.job-info-card__value {
  font-family: var(--font-brand);
  font-size: 13px;
  font-weight: 600;
  color: var(--white);
  text-align: right;
}

@media (max-width: 900px) {
  .job-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .job-body {
    flex-direction: column;
  }

  .job-sidebar {
    width: 100%;
  }
}
</style>
